import React, { useEffect, useRef } from 'react';

import { isTruthy } from '@app/common/utils';
import { createKeyNavHandler } from '@app/common/utils/focus/keynav';
import { tryFocusFirstElement } from '@app/common/utils/focus/movement';

import styles from './ActionGroup.pcss';

/**
 * Action group root element type.
 *
 * List type is not allowed due to action group item should have `group` role.
 */
export type ActionGroupElement = HTMLDivElement;

/**
 * Action group props.
 */
export interface ActionGroupProps extends React.HTMLAttributes<ActionGroupElement> {
  /**
   * Child items.
   *
   * Child items will be cloned with extended props.
   *
   * In most cases it should be an array of {@link ActionGroupItem}.
   */
  readonly children: React.ReactElement | React.ReactElement[];
  /**
   * First child item will be focused automatically if set to `true`.
   */
  readonly autoFocus?: boolean;
}

/**
 * Action group.
 *
 * A keyboard navigable vertical group of actions.
 *
 * Adds proper `Arrow Up`, `Arrow Down`, `Home`, and `End` keys handlers.
 * Also maintains focus loop when user navigates with arrows.
 *
 * Can be disabled by using the `aria-disabled` attribute.
 *
 * Primarily used with {@link ActionGroupItem} as children:
 * ```tsx
 * <ActionGroup>
 *   <ActionGroupItem onSelect={() => ('Action 1')}>
 *     Action 1
 *   </ActionGroupItem>
 *   <ActionGroupItem onSelect={() => ('Action 2')}>
 *     Action 2
 *   </ActionGroupItem>
 *   <ActionGroupItem onSelect={() => ('Action 3')}>
 *     Action 3
 *   </ActionGroupItem>
 * <ActionGroup>
 * ```
 */
export const ActionGroup: React.FC<ActionGroupProps> = (props) => {
  const disabledAttribute = 'aria-disabled';
  const childTabIndexDefault = 0;
  const childTabIndexDisabled = -1;

  const { autoFocus, children } = props;

  // Reference to the root element
  const ref = useRef<ActionGroupElement>(null);

  // Autofocus handing
  useEffect(() => {
    if (autoFocus) {
      tryFocusFirstElement(ref.current);
    }
  }, [autoFocus]);

  return (
    <div ref={ref} className={styles.group} role="group">
      {React.Children.map<React.ReactElement, React.ReactElement>(children, (child, index) => {
        // Child item is considered disabled whenever itself or the whole action group is disabled
        const isDisabled = isTruthy(child.props[disabledAttribute]) || isTruthy(props[disabledAttribute]);
        // Child item props should be extended to handle keyboard navigation properly
        const extendedProps = {
          ...child.props,
          key: index,
          // Exclude disabled items from keyboard navigation
          tabIndex: isDisabled ? childTabIndexDisabled : (child.props.tabIndex || childTabIndexDefault),
          onKeyDown: child.props.onKeyDown || createKeyNavHandler(ref),
          [disabledAttribute]: isDisabled,
        };
        // Clone child item to extend its properties
        return React.cloneElement(child, extendedProps);
      })}
    </div>
  );
};
